import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth, useLocale } from '../../context';
import { api } from '../../api';
import { currencyCode } from '../../utils/currency';
import { formatMoney } from '../../utils/currency';
import '../../styles/registration.css';

function IconAttach() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M11.5 4.5L7.8 8.2a2.5 2.5 0 103.5 3.5l4.5-4.5a4 4 0 10-5.7-5.7L5.9 8.3a5.5 5.5 0 107.8 7.8l5.3-5.3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

const ALLOWED_ATTACHMENT_MIME = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/pdf',
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'video/3gpp',
]);

function maxBytesForAttachment(file) {
  return String(file?.type || '').toLowerCase().startsWith('video/')
    ? 25 * 1024 * 1024
    : 7 * 1024 * 1024;
}

function formatOperationDate(value) {
  const date = new Date(value);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function isWithdrawalType(typeKey) {
  return String(typeKey || '').startsWith('withdrawal_');
}

function FieldLabel({ label, required }) {
  if (!label) return null;
  return (
    <div className="mobile-request-form__label">
      {label}
      {required ? <span className="mobile-request-form__required"> *</span> : null}
    </div>
  );
}

export default function SupportDynamicForm({
  template,
  typeKey,
  onSubmit,
  onCancel,
  loading,
  error,
  submitLabel,
  cancelLabel,
  className = 'mobile-request-form registration--mobile-auth',
}) {
  const { token } = useAuth();
  const { locale, t } = useLocale();
  const fileRef = useRef(null);
  const fields = template?.formSchema?.fields || [];

  const [values, setValues] = useState({});
  const [files, setFiles] = useState([]);
  const [operations, setOperations] = useState([]);
  const [operationsOpen, setOperationsOpen] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setValues({});
    setFiles([]);
    setOperationsOpen(false);
    setUploadError('');
  }, [typeKey]);

  useEffect(() => {
    if (!token || !fields.some((field) => field.type === 'operation')) return;
    api.historyTransactions(token)
      .then((items) => {
        const filtered = (items || []).filter((item) => {
          const kind = String(item.kind || item.type || '').toLowerCase();
          if (isWithdrawalType(typeKey)) return kind.includes('withdraw') || Number(item.amount) < 0;
          return kind.includes('deposit') || Number(item.amount) > 0;
        });
        setOperations(filtered.slice(0, 30));
      })
      .catch(() => setOperations([]));
  }, [token, typeKey, fields]);

  const messageField = fields.find((field) => field.id === 'message');
  const messageMax = messageField?.maxLength || 1500;
  const messageValue = values.message || '';
  const remaining = messageMax - messageValue.length;

  const canSubmit = useMemo(() => {
    for (const field of fields) {
      if (!field.required) continue;
      if (field.type === 'files') continue;
      const value = values[field.id];
      if (value == null || String(value).trim() === '') return false;
    }
    return true;
  }, [fields, values]);

  const setField = (id, value) => setValues((current) => ({ ...current, [id]: value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit || loading || uploading) return;
    setUploadError('');
    let attachments = [];
    if (files.length) {
      if (!token) {
        setUploadError(t('account.requests.error') || 'Oturum gerekli');
        return;
      }
      setUploading(true);
      try {
        const uploaded = await api.uploadSupportAttachments(token, files);
        attachments = uploaded?.items || uploaded?.attachments || [];
        if (files.length && !attachments.length) {
          setUploadError(t('account.requests.form.uploadFailed') || 'Dosyalar yüklenemedi');
          return;
        }
      } catch (err) {
        setUploadError(err?.message || t('account.requests.form.uploadFailed') || 'Dosyalar yüklenemedi');
        return;
      } finally {
        setUploading(false);
      }
    }
    const payload = { ...values, attachments };
    onSubmit?.({
      type_key: typeKey,
      subject: template?.label,
      payload,
      locale,
    });
  };

  const onPickFiles = (event, field) => {
    const maxFiles = field.maxFiles || 5;
    const picked = Array.from(event.target.files || []);
    const next = [];
    for (const file of picked) {
      const mime = String(file.type || '').toLowerCase();
      if (!ALLOWED_ATTACHMENT_MIME.has(mime)) {
        setUploadError(t('account.requests.form.unsupportedFile') || 'Desteklenmeyen dosya türü');
        continue;
      }
      const maxBytes = maxBytesForAttachment(file);
      if (file.size > maxBytes) {
        setUploadError(mime.startsWith('video/')
          ? (t('account.requests.form.videoTooLarge') || 'Video en fazla 25 MB olabilir')
          : (t('account.requests.form.fileTooLarge') || 'Dosya en fazla 7 MB olabilir'));
        continue;
      }
      next.push(file);
    }
    if (next.length) setUploadError('');
    setFiles((current) => [...current, ...next].slice(0, maxFiles));
    event.target.value = '';
  };

  const renderField = (field) => {
    if (field.type === 'textarea') {
      return (
        <div key={field.id} className="mobile-request-form__textarea-wrap">
          <FieldLabel label={field.label} required={field.required} />
          <div className="mobile-request-form__textarea-box">
            <textarea
              className="mobile-request-form__textarea"
              placeholder={field.placeholder || ''}
              value={messageValue}
              maxLength={messageMax}
              onChange={(event) => setField('message', event.target.value)}
            />
          </div>
          <div className="mobile-request-form__counter">
            {t('account.requests.form.symbolsRemaining', { count: remaining })}
          </div>
        </div>
      );
    }

    if (field.type === 'files') {
      return (
        <div key={field.id} className="mobile-request-form__attach">
          <input
            ref={fileRef}
            type="file"
            className="mobile-request-form__file-input"
            accept={field.accept || '.jpg,.jpeg,.png,.pdf,.mp4,.mov,.webm'}
            multiple
            onChange={(event) => onPickFiles(event, field)}
          />
          <button
            type="button"
            className="mobile-request-form__upload"
            onClick={() => fileRef.current?.click()}
          >
            <span className="mobile-request-form__upload-icon" aria-hidden="true"><IconAttach /></span>
            <span className="mobile-request-form__upload-text">
              <span className="mobile-request-form__upload-caption">
                {t('account.requests.form.dragOrUpload')}
              </span>
              <span className="mobile-request-form__upload-desc">
                {field.hint || t('account.requests.form.attachHint')}
              </span>
            </span>
          </button>
          {files.length > 0 && (
            <div className="mobile-request-form__file-list">
              {files.map((file) => (
                <span key={`${file.name}-${file.lastModified}`} className="mobile-request-form__file-chip">{file.name}</span>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (field.type === 'operation') {
      return (
        <div key={field.id} className="mobile-request-form__operation-box">
          <FieldLabel label={field.label} required={field.required} />
          {field.description ? <div className="mobile-request-form__operation-desc">{field.description}</div> : null}
          <button
            type="button"
            className={`mobile-request-form__operation-btn${values.operation ? ' is-filled' : ''}`}
            onClick={() => setOperationsOpen((open) => !open)}
          >
            {values.operation || t('account.requests.form.choose') || field.label}
          </button>
          {operationsOpen && (
            <div className="mobile-request-form__operation-list">
              {operations.length === 0 ? (
                <div className="mobile-request-form__operation-empty">{t('account.requests.form.noOperations')}</div>
              ) : (
                operations.map((item) => {
                  const label = `${formatOperationDate(item.createdAt || item.created_at)} · ${formatMoney(Math.abs(Number(item.amount || 0)))}`;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`mobile-request-form__operation-item${values.operation === label ? ' is-selected' : ''}`}
                      onClick={() => {
                        setField('operation', label);
                        setOperationsOpen(false);
                      }}
                    >
                      {label}
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>
      );
    }

    return (
      <div key={field.id} className="container--CCNx7 container_medium label--SNSj8 mobile-request-form__field">
        <FieldLabel label={field.label} required={field.required} />
        <div className="inputContainer inputContainer_large">
          <div className="inputLine inputLine_leftOffset inputLine_rightOffset inputLine_withoutBottomOffset">
            <div className="inputLine__content">
              <input
                type="text"
                className="input"
                inputMode={field.type === 'amount' ? 'decimal' : field.inputMode || 'text'}
                placeholder={field.placeholder || ''}
                value={values[field.id] || ''}
                onChange={(event) => setField(field.id, event.target.value)}
                autoComplete="off"
              />
            </div>
            {field.type === 'amount' ? (
              <span className="mobile-request-form__input-icon">{currencyCode()}</span>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  if (!template) return null;

  return (
    <form className={className} onSubmit={handleSubmit} noValidate>
      {fields.filter((field) => field.type !== 'textarea' && field.type !== 'files').map(renderField)}
      {fields.filter((field) => field.type === 'textarea' || field.type === 'files').map(renderField)}

      {error || uploadError ? <div className="mobile-request-form__error">{error || uploadError}</div> : null}

      <div className="mobile-request-form__actions">
        <button type="submit" className="mobile-request-form__submit" disabled={loading || uploading || !canSubmit}>
          {loading || uploading ? '...' : submitLabel}
        </button>
        {onCancel ? (
          <button type="button" className="mobile-request-form__cancel" onClick={onCancel}>
            {cancelLabel}
          </button>
        ) : null}
      </div>
    </form>
  );
}
